import {
  ActionItem,
  ActionList,
  renderApproveContracts,
} from '@/presentation/components/modules/ActionList';
import { Address, parseEther } from 'viem';
import { useTranslations } from 'use-intl';
import { LiquidityPosition, TokenId, Zero } from 'nftx.js';
import { useNetwork } from '@nftx/react';
import { IncreaseLiquidityDetails } from '../types';
import useIncreaseLiquidity from '@/application/write/positions/useIncreaseLiquidity';
import useIncreateLiquidityQuote from '@/application/read/positions/useIncreaseLiquidityQuote';
import { useMemo } from 'react';

export interface ProcessingProps {
  state: IncreaseLiquidityDetails;
  symbol: string;
  userAddress: Address | undefined;
  poolType: 'eth' | 'nft' | 'pair';
  position: LiquidityPosition;
  onCancel: () => void;
  onComplete: () => void;
}

// TODO: needs intl specific to increasing liquidity

export const Processing = ({
  state,
  symbol,
  userAddress,
  poolType,
  position,
  onCancel,
  onComplete,
}: ProcessingProps) => {
  const network = useNetwork();
  const t = useTranslations('IncreaseLiquidityPosition.Processing');
  const [write, { status, error }] = useIncreaseLiquidity();
  const { data: quote } = useIncreateLiquidityQuote(
    useMemo(() => {
      const tokenIds = state.items.map(
        ({ tokenId, quantity }) =>
          [tokenId, Number(quantity ?? 0n)] as [TokenId, number]
      );

      const hasEth = poolType === 'eth' || poolType === 'pair';
      const hasNft =
        (poolType === 'nft' || poolType === 'pair') && state.using === 'nft';
      const hasVToken =
        (poolType === 'nft' || poolType === 'pair') && state.using === 'vtoken';

      const ethToPair = hasEth ? parseEther(state.ethAmount) : Zero;
      const vTokenToPair = hasVToken ? parseEther(state.tokenAmount) : Zero;
      const nftsToPair = hasNft ? parseEther(state.tokenAmount) : Zero;
      const lowerPrice = parseEther(state.minPriceInEth);
      const upperPrice = parseEther(state.maxPriceInEth);

      return {
        liquidity: {
          ethToPair,
          lowerPrice,
          nftsToPair,
          upperPrice,
          vTokenToPair,
        },
        tokenIds,
        userAddress,
        positionId: position.id,
      };
    }, [poolType, position.id, state, userAddress])
  );

  if (!quote) {
    return null;
  }

  const actionItems: JSX.Element[] = [];

  actionItems.push(
    ...renderApproveContracts({
      approveContracts: quote.approveContracts,
      index: actionItems.length,
      network,
      symbol,
    })
  );

  // Increase liquidity
  actionItems.push(
    <ActionItem
      key={actionItems.length}
      index={actionItems.length}
      label="Increase liquidity"
      status={status}
      error={error}
      action={() => {
        if (!userAddress) {
          return;
        }

        write({
          quote,
          userAddress,
        });
      }}
    />
  );

  return (
    <div className="max-w-md mx-auto">
      <ActionList
        title={t('title')}
        description={t('description', { symbol })}
        onCancel={onCancel}
        onComplete={onComplete}
      >
        {actionItems}
      </ActionList>
    </div>
  );
};

export default Processing;
